/**
 * StatCard Component
 *
 * A dashboard statistics card with icon, value, trend indicator,
 * and optional action button. Follows AdminDashboardPage patterns.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAmount } from '../../utils/numberFormat';

export interface StatCardProps {
  title: string;
  value: string | number;
  icon?: string;
  iconBgColor?: string;
  iconColor?: string;
  trend?: {
    value: number;
    label?: string;
    isPositive?: boolean;
  };
  subtitle?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  loading?: boolean;
  className?: string;
}

export default function StatCard({
  title,
  value,
  icon,
  iconBgColor = 'bg-[var(--pv-color-primary)]',
  iconColor = 'text-white',
  trend,
  subtitle,
  action,
  loading = false,
  className = '',
}: StatCardProps) {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const formatValue = (val: string | number) => {
    if (typeof val === 'number') {
      return formatAmount(val);
    }
    return val;
  };

  if (loading) {
    return (
      <div className={`bg-white dark:bg-[var(--pv-color-surface-strong)] rounded-2xl p-4 border border-[var(--pv-border-soft)] ${className}`}>
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-3">
            <div className="h-4 bg-gray-200 dark:bg-[var(--pv-color-surface-soft)] rounded w-24"></div>
            <div className="h-10 w-10 bg-gray-200 dark:bg-[var(--pv-color-surface-soft)] rounded-full"></div>
          </div>
          <div className="h-8 bg-gray-200 dark:bg-[var(--pv-color-surface-soft)] rounded w-20 mb-2"></div>
          <div className="h-3 bg-gray-200 dark:bg-[var(--pv-color-surface-soft)] rounded w-16"></div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white dark:bg-[var(--pv-color-surface-strong)] rounded-2xl p-4 border border-[var(--pv-border-soft)] transition-all hover:shadow-lg ${className}`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-[var(--pv-color-text-muted)] font-medium">
          {title}
        </span>
        {icon && (
          <div className={`flex h-10 w-10 items-center justify-center rounded-full ${iconBgColor} ${iconColor}`}>
            <i className={`${icon} text-xl`}></i>
          </div>
        )}
      </div>

      <div className="space-y-1">
        <div className="text-2xl font-bold text-[var(--pv-color-text)]">
          {formatValue(value)}
        </div>

        {(trend || subtitle) && (
          <div className="flex items-center gap-2 text-sm">
            {trend && (
              <span
                className={`inline-flex items-center gap-0.5 font-medium ${
                  trend.isPositive !== false
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }`}
              >
                <i
                  className={
                    trend.isPositive !== false
                      ? 'ri-arrow-up-line'
                      : 'ri-arrow-down-line'
                  }
                ></i>
                {Math.abs(trend.value)}%
                {trend.label && (
                  <span className="text-[var(--pv-color-text-muted)] font-normal ms-1">
                    {trend.label}
                  </span>
                )}
              </span>
            )}
            {subtitle && !trend && (
              <span className="text-[var(--pv-color-text-muted)]">{subtitle}</span>
            )}
          </div>
        )}
      </div>

      {action && (
        <button
          onClick={action.onClick}
          className="mt-3 text-sm text-[var(--pv-color-primary)] hover:text-[var(--pv-color-primary-dark)] font-medium transition-colors"
        >
          {action.label}
          <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-line ms-1`}></i>
        </button>
      )}
    </div>
  );
}

// Compact variant for inline stats
export function StatInline({
  label,
  value,
  icon,
  className = '',
}: {
  label: string;
  value: string | number;
  icon?: string;
  className?: string;
}) {
  const formatValue = (val: string | number) => {
    if (typeof val === 'number') {
      return formatAmount(val);
    }
    return val;
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {icon && (
        <i className={`${icon} text-[var(--pv-color-primary)]`}></i>
      )}
      <span className="text-[var(--pv-color-text-muted)] text-sm">{label}:</span>
      <span className="font-semibold text-[var(--pv-color-text)]">{formatValue(value)}</span>
    </div>
  );
}

// Stats grid wrapper
export function StatsGrid({
  children,
  columns = 4,
  className = '',
}: {
  children: ReactNode;
  columns?: 2 | 3 | 4;
  className?: string;
}) {
  const gridClasses = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={`grid gap-4 ${gridClasses[columns]} ${className}`}>
      {children}
    </div>
  );
}
